/**
 * Free-typed sentence production — the hardest card in the game (TASKS T53, SPEC §5 tier 5, §4 PC).
 *
 * An English sentence, an empty box, and nothing else. No tiles to arrange, no first letter, no
 * length, **no scaffold at all** — `scaffold` is `null` and stays `null`, whatever the run's
 * escalation curve says a card at this point may offer (T54 honours a null scaffold by rendering no
 * reveal control). This is the desk-context default the brief names: *"free-typed recall with no
 * tiles and no scaffolds"*.
 *
 * Nothing else here is complicated, and that is the point. The mode's whole substance is what it
 * refuses to put on the card.
 *
 * ## Why a new item is never asked this way
 *
 * A phrase the learner has never seen cannot be free-typed; asking is not difficulty, it is a
 * guaranteed failure that costs the item an FSRS lapse it did not earn. SPEC §5 sets the order for
 * tier 5 as "tiles → free-typed", and `playableModes` enforces it: on an item's first exposure the
 * free-typed mode is withheld while any alternative exists. The escalation curve then does the rest
 * — tiles early, free-typed later.
 *
 * Pure. No DOM, no clock, no store.
 */

import type { ModeInput, Presentation } from './types';

export function sentencePresentation(input: ModeInput): Presentation {
  const { item } = input;
  return {
    mode: 'sentence',
    instruction: 'Type the whole sentence',
    segments: [{ kind: 'text', text: item.en }],
    // No gloss: the prompt is already the English. There is nothing else to give.
    gloss: null,
    expected: item.es,
    /*
     * Null, always, and not "null for now". A scaffold on this card would make it a different card —
     * the one below it on the ladder — and the ladder's top rung has to be somewhere.
     */
    scaffold: null,
    input: 'text',
    fieldLabel: 'The sentence',
    announce: `${item.en}. Type the whole sentence in Spanish.`,
  };
}
